import type { ContactFieldDto, SegmentDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Dialog } from '../../ui/Dialog.js'
import { Field } from '../../ui/Field.js'
import { Declared } from '../contacts/ContactFields.js'

/**
 * Editing a segment without opening its rules.
 *
 * Its name, and whatever columns this organisation declared on its segments.
 * Not the rules: those are the audience, they need a builder, and a dialog over
 * a list is the wrong place to change who a campaign reaches.
 *
 * **A name is not an audience, and neither is a declared column.** Every
 * campaign pointed at this segment keeps reaching exactly the same people —
 * what it reaches is its rules and its members, and nothing here touches
 * either. The dialog says so, because "will this change who gets my next send"
 * is the question somebody editing an audience actually has.
 *
 * Each declared column is asked for the way its kind wants to be asked, through
 * the same `Declared` control the contact form uses. A second set of controls
 * per kind would be a second answer to what a status column offers.
 */
export function RenameSegmentDialog({
  segment,
  fields,
  busy,
  onSave,
  onClose,
}: {
  readonly segment: SegmentDto
  /** The columns declared on segments, so the form asks for every one of them. */
  readonly fields: readonly ContactFieldDto[]
  readonly busy: boolean
  readonly onSave: (name: string, custom: Readonly<Record<string, string>>) => void
  readonly onClose: () => void
}) {
  const [name, setName] = useState(segment.name)
  const [custom, setCustom] = useState<Readonly<Record<string, string>>>(segment.custom)

  const unchanged =
    name.trim() === segment.name && JSON.stringify(custom) === JSON.stringify(segment.custom)

  const save = () => {
    onSave(name.trim(), custom)
    onClose()
  }

  return (
    <Dialog heading="Edit segment" onClose={onClose}>
      <form
        id="edit-segment"
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
      >
        <Field
          disabled={busy}
          hint="Who this segment reaches does not change."
          id="segment-name"
          label="Name"
          onChange={setName}
          type="text"
          value={name}
        />

        {fields.map((field) => (
          <Declared
            busy={busy}
            field={field}
            id={`segment-${field.key}`}
            key={field.key}
            onChange={(value) => setCustom((current) => ({ ...current, [field.key]: value }))}
            value={custom[field.key] ?? ''}
          />
        ))}
      </form>

      <div className="mt-5 flex items-center gap-3 border-line border-t pt-4">
        <button
          className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
          disabled={busy || name.trim() === '' || unchanged}
          form="edit-segment"
          type="submit"
        >
          Save
        </button>
        <button
          className="font-medium text-[13px] text-t3 hover:text-t1"
          onClick={onClose}
          type="button"
        >
          Cancel
        </button>
      </div>
    </Dialog>
  )
}
